import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthUser, getMe, login as apiLogin, logout as apiLogout } from '@/api/auth';

interface AuthContextValue {
  user:          AuthUser | null;
  loading:       boolean;
  isAdmin:       boolean;
  hasPermission: (p: string) => boolean;
  login:         (username: string, password: string) => Promise<void>;
  logout:        () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMe()
      .then(setUser)
      .finally(() => setLoading(false));
  }, []);

  const login = async (username: string, password: string) => {
    const u = await apiLogin(username, password);
    setUser(u);
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  const isAdmin = user?.role === 'Admin';

  /** Admin has all permissions; others check the permissions array. */
  const hasPermission = (p: string): boolean => {
    if (!user) return false;
    if (isAdmin) return true;
    return (user.permissions ?? []).includes(p);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, hasPermission, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
